import type { Cents } from '@shared/money/cents'

/**
 * A variação contra o mês anterior — o "↘ 5,4%" de `Docs/04-Design/Telas/Dashboard.md`.
 *
 * ## Compara GRANDEZAS, não os números com sinal
 *
 * `totalOutflow` devolve negativo, como as transações que soma (decisão 141e).
 * Uma variação calculada sobre os números com sinal daria a resposta certa por
 * acidente enquanto os dois meses fossem negativos, e inverteria a seta no mês
 * em que um deles não fosse — que é justamente o mês raro em que alguém olha.
 * A conta é sobre `|atual|` e `|anterior|`, e a direção sai daí.
 *
 * ## Não há variação quando o mês anterior é ZERO
 *
 * Divisão por zero não tem resposta, e "+∞%" não é informação: é ruído com
 * cara de dado. Também não é "+100%" — gastar R$ 300 depois de um mês sem nada
 * não é um aumento de 100%, é o primeiro mês com gasto. A função devolve `null`
 * e quem chama mostra a ausência, em vez de inventar um número.
 *
 * Isso acontece de verdade: o primeiro mês da base tem 10 transações, e o mês
 * anterior a ele **não existe**.
 *
 * ## A direção é sobre a GRANDEZA, e o que ela significa depende de quem chama
 *
 * `'up'` quer dizer *o número cresceu*, e nada mais. Num gasto isso é ruim; numa
 * entrada é bom. Esta função não sabe qual é qual, e não deve saber — a tela é
 * que escolhe a cor. Um `'good'`/`'bad'` aqui embutiria uma leitura de produto
 * num módulo de aritmética.
 */

export type Variation = {
  /**
   * A variação em pontos percentuais, **arredondada a uma casa** — 5,4% é
   * `5.4`. Sempre positivo: o sinal está em `direction`, e ter o mesmo fato em
   * dois lugares é como eles passam a discordar.
   */
  percent: number
  direction: 'up' | 'down' | 'flat'
}

/**
 * `null` quando o mês anterior é zero. Ver o cabeçalho.
 *
 * O arredondamento é a **uma casa**, e acontece antes da comparação com zero:
 * uma diferença de meio centavo em cinquenta mil reais é `0.0%`, e mostrar
 * "↗ 0,0%" seria afirmar um movimento que ninguém sentiu. Arredondado a zero, é
 * `'flat'`.
 */
export function variationOf(current: Cents, previous: Cents): Variation | null {
  const atual = Math.abs(current)
  const anterior = Math.abs(previous)
  if (anterior === 0) return null

  const bruto = ((atual - anterior) / anterior) * 100
  const percent = Math.round(Math.abs(bruto) * 10) / 10
  if (percent === 0) return { percent: 0, direction: 'flat' }
  return { percent, direction: bruto > 0 ? 'up' : 'down' }
}

/** `'↗ 5,4%'`. A vírgula é decimal, como todo número desta casa. */
export function variationLabel(variation: Variation): string {
  const seta = variation.direction === 'up' ? '↗' : variation.direction === 'down' ? '↘' : '→'
  return `${seta} ${variation.percent.toFixed(1).replace('.', ',')}%`
}
